import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/JobFormPage.css";
import JobStartModal from "../components/JobstartModal";
import Jobpreviewpage from "../components/Jobpreviewpage";
import { FiSave } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";
import { FaPaperPlane } from "react-icons/fa"; // for Publish Job button
import { addJob } from "../services/jobsApi";


const JobFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Move savedJob so it's available for useEffects below
  const savedJob = JSON.parse(localStorage.getItem("savedJob") || "null");

  useEffect(() => {
    // fixed variable usage: check savedDraft (was checking savedJob accidentally)
    const savedDraft = JSON.parse(localStorage.getItem("jobDraftData") || "null");
    if (savedJob) {
      setForm(savedJob); // Load filled job for editing
    }
    const editData = JSON.parse(localStorage.getItem("editJobFormData") || "null");
    const skipModal = localStorage.getItem("skipStartModal") === "true";

    if (skipModal && editData) {
      // Prefill form for edit mode
      setForm(editData);
      setShowStartModal(false); // ✅ Hide modal if editing
    } else if (savedDraft) {
      // Prefill if draft exists
      setForm(savedDraft);
    }
  }, [location.state]); // keep dependency as you had

  const [form, setForm] = useState({
    jobTitle: "",
    jobType: "",
    location: "",
    category: "",
    description: "",
    requirements: "",
    salaryRange: "",
    experience: "",
    contactEmail: "",
    qualifications: "",
    skills: [],
    openings: "",
    deadline: "",
  });

  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCustomRole, setIsCustomRole] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [showStartModal, setShowStartModal] = useState(false);
  const [fromEditMode, setFromEditMode] = useState(false);
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [showCustomJobTitle, setShowCustomJobTitle] = useState(false);
  const [filteredRoles, setFilteredRoles] = useState([]);

  const wrapperRef = useRef(null); // for click-outside

  const predefinedRoles = [
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Engineer",
    "UI/UX Designer",
    "Data Scientist",
    "AI Engineer",
    "DevOps Engineer",
    "Project Manager",
    "QA Tester",
    "Business Analyst",
    "Digital Marketer",
    "HR Manager",
    "Finance Executive",
  ];

  const parsePDFDataToForm = (data) => {
    const text = data.text || "";

    return {
      jobTitle: extractJobTitle(text),
      description: extractDescription(text),
      requirements: extractRequirements(text),
      salaryRange: extractSalary(text),
      skills: extractSkills(text),
      //experience: extractExperience(text),
      //qualifications: extractQualifications(text),
      //location: extractLocation(text),
      //openings: extractOpenings(text),
      //deadline: "",
      contactEmail: extractEmail(text),
      jobType: "",
      category: ""
    };
  };

  const extractJobTitle = (text) => {
    const line = text.split("\n")[0];
    return line.length < 60 ? line : "";
  };

  const extractDescription = (text) => {
    const match = text.match(/description[:\-]?([\s\S]*?)requirements/i);
    return match ? match[1].trim() : "";
  };

  const extractRequirements = (text) => {
    const match = text.match(/requirements[:\-]?([\s\S]*?)skills/i);
    return match ? match[1].trim() : "";
  };

  const extractSkills = (text) => {
    const predefined = [
      "react", "node", "javascript", "python", "aws",
      "docker", "kubernetes", "figma", "git", "sql"
    ];

    return predefined.filter(skill =>
      text.toLowerCase().includes(skill.toLowerCase())
    );
  };

  const extractSalary = (text) => {
    const match = text.match(/₹?[\d,]+\s*-\s*₹?[\d,]+/);
    return match ? match[0] : "";
  };

  const extractEmail = (text) => {
    const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z.-]+\.[A-Za-z]{2,}/);
    return match ? match[0] : "";
  };


  const predefinedSkills = [
    "React",
    "Node.js",
    "Angular",
    "Django",
    "Javascript",
    "Python",
    "Machine Learning",
    "SQL",
    "AWS",
    "Docker",
    "Kubernetes",
    "Figma",
    "Git",
    "Communication",
    "Leadership",
  ];

  // ✅ Restore saved draft from localStorage
  useEffect(() => {
    const savedDraft = localStorage.getItem("jobDraftData");
    if (savedDraft) {
      setForm(JSON.parse(savedDraft));
    }
  }, []);

  useEffect(() => {
    const savedForm = localStorage.getItem("draftJobForm");
    const editMode = localStorage.getItem("editMode");
    if (!editMode) {
      // Show start modal only if not returning from edit mode
      setShowStartModal(true);
    }
    if (savedForm) {
      setForm(JSON.parse(savedForm));
    }
    if (editMode) {
      setFromEditMode(true);
      localStorage.removeItem("editMode");
    }
  }, []);

  // ✅ show modal only if not skipped before
  useEffect(() => {
    const skipModal = localStorage.getItem("skipStartModal");
    if (!skipModal || skipModal === "false") {
      setShowStartModal(true);
    }
  }, []);

  // click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setFilteredRoles([]); // hide suggestion list
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ when modal is closed manually
  const handleCloseModal = () => {
    setShowStartModal(false);
    localStorage.setItem("skipStartModal", "true");
  };

  // ✅ Save draft to localStorage
  // const handleSaveDraft = () => {
  //   localStorage.setItem("jobDraftData", JSON.stringify(form));
  // localStorage.removeItem("skipStartModal"); // show modal next time
  //alert("💾 Draft saved successfully!");
  //};

  const handleSaveDraft = () => {
    const draftJobs = JSON.parse(localStorage.getItem("postedJobs") || "[]");

    const draftJob = {
      ...form,
      id: Date.now(),
      status: "draft",
      category: "draft",
      createdAt: new Date().toISOString(),
    };

    draftJobs.push(draftJob);
    localStorage.setItem("postedJobs", JSON.stringify(draftJobs));
    localStorage.setItem("jobDraftData", JSON.stringify(form));

    alert("💾 Draft saved successfully!");
  };


  // ✅ When user wants to edit (coming from preview)
  const handleEdit = () => {
    setShowStartModal(false);
    localStorage.setItem("skipStartModal", "true");
  };

  // ✅ Reset modal flag after publish or save
  const handlePublish = () => {
    alert("✅ Job Published Successfully!");
    localStorage.removeItem("skipStartModal"); // show modal next time
    navigate("/recruiter-dashboard");
  };

  const handleAutofill = (parsedData) => {
    const mapped = parsePDFDataToForm(parsedData);
    setForm((prev) => ({ ...prev, ...mapped }));
    setShowStartModal(false);
  };


  const handleManual = () => {
    setShowStartModal(false);
  };

  // Updated handleJobTitleChange -> accepts either event or string
  const handleJobTitleChange = (eOrStr) => {
    let text;
    if (typeof eOrStr === "string") {
      text = eOrStr;
    } else if (eOrStr && eOrStr.target) {
      text = eOrStr.target.value;
    } else {
      text = "";
    }

    setJobTitleInput(text);
    setShowCustomJobTitle(false);

    // If input empty -> hide suggestions
    if (text.trim() === "") {
      setFilteredRoles([]);
      setForm((prev) => ({ ...prev, jobTitle: "" }));
      return;
    }

    const matches = predefinedRoles.filter((role) =>
      role.toLowerCase().includes(text.toLowerCase())
    );

    if (matches.length === 0) {
      // show "Others" suggestion when nothing matches
      setFilteredRoles(["Others"]);
    } else {
      setFilteredRoles(matches);
    }

    setForm((prev) => ({ ...prev, jobTitle: text }));
  };

  const handleSelectJobRole = (role) => {
    if (role === "Others") {
      setShowCustomJobTitle(true);
      setFilteredRoles([]);
      setJobTitleInput("");
      setForm((prev) => ({ ...prev, jobTitle: "" }));
    } else {
      setJobTitleInput(role);
      setFilteredRoles([]);
      setForm((prev) => ({ ...prev, jobTitle: role }));
    }
  };

  // AI Suggestion logic (mock for now)
  const handleAISuggestion = async () => {
    setLoadingAI(true);
    setTimeout(() => {
      const jobTitle = form.jobTitle || "the selected role";
      setForm((prev) => ({
        ...prev,
        description: `As a ${jobTitle}, you will be responsible for developing, testing, and maintaining scalable solutions to meet client and business goals.`,
        requirements: `• Expertise in relevant frameworks and tools\n• Strong understanding of agile methodologies\n• Excellent communication and teamwork`,
        skills: ["React", "Node.js", "Git", "Agile", "Problem Solving", "Python"],
      }));
      setLoadingAI(false);
      //alert("✨ AI suggestions added successfully!");
    }, 1500);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Handle Skill Input with Chips
  const handleSkillInputChange = (e) => {
    const value = e.target.value;
    setSkillInput(value);

    if (value.trim() === "") {
      setSkillSuggestions([]);
      return;
    }

    const filtered = predefinedSkills.filter(
      (skill) =>
        skill.toLowerCase().includes(value.toLowerCase()) &&
        !form.skills.includes(skill)
    );
    setSkillSuggestions(filtered);
  };

  const handleAddSkill = (skill) => {
    if (skill && !form.skills.includes(skill)) {
      setForm({ ...form, skills: [...form.skills, skill] });
    }
    setSkillInput("");
    setSkillSuggestions([]);
  };

  const handleRemoveSkill = (skill) => {
    setForm({
      ...form,
      skills: form.skills.filter((s) => s !== skill),
    });
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter" && skillInput.trim()) {
      e.preventDefault();
      handleAddSkill(skillInput.trim());
    }
  };

  //const handlePreview = () => {
  // localStorage.setItem("jobFormData", JSON.stringify(form));
  //navigate("/job-preview");
  //};

  //const handleSubmit = (e) => {
  //e.preventDefault();
  //alert("✅ Job posted successfully!");
  //navigate("/posted-jobList");
  //localStorage.removeItem("jobDraftData");
  //};

  const handlePreview = () => {
    localStorage.setItem("viewJob", JSON.stringify(form));
    navigate("/job-preview");
  };


  {/*const handleSubmit = (e) => {
    e.preventDefault();

    const existingJobs = JSON.parse(localStorage.getItem("postedJobs") || "[]");

    const editJob = JSON.parse(localStorage.getItem("editJobFormData") || "null");

    const newJob = {
      ...form,
      id: editJob?.id || Date.now(),
      status: "under_review",
      category: "published",
      createdAt: editJob?.createdAt || new Date().toISOString(),
      views: Math.floor(Math.random() * 50),
      applicants: Math.floor(Math.random() * 20),
    };

    let updatedJobs;

    if (editJob) {
      // Update existing job
      updatedJobs = existingJobs.map((job) =>
        job.id === editJob.id ? newJob : job
      );
      localStorage.removeItem("editJobFormData");
    } else {
      // Add new job
      updatedJobs = [...existingJobs, newJob];
    }

    localStorage.setItem("postedJobs", JSON.stringify(updatedJobs));

    // cleanup
    localStorage.removeItem("jobDraftData");
    localStorage.removeItem("draftJobForm");

    alert("✅ Job posted successfully!");
    navigate("/posted-jobList");
  };*/}
  // added on 27-11-2025
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await addJob(form);

      alert("✅ Job posted to sucessfully!");
      localStorage.removeItem("jobDraftData");
      localStorage.removeItem("draftJobForm");

      navigate("/posted-jobList");
    } catch (err) {
      console.error("Job post failed:", err);
      alert("❌ Failed to post job to backend");
    }
  };


  return (
    <>
      {/* ✅ Show modal only when needed */}
      {showStartModal && !fromEditMode && (
        <JobStartModal
          onAutofill={handleAutofill}
          onManual={handleManual}
          onClose={() => setShowStartModal(false)}
        />
      )}

      <div className="job-form-container">
        <div className="job-form-card">
          <h2 className="job-form-title">Post a New Job</h2>
          <p className="job-form-subtitle">
            Fill in the job details below to publish your listing.
          </p>

          <form onSubmit={handleSubmit}>
            {/* Job Information Section */}
            <section className="form-section">
              <h3 className="section-title">Job Information</h3>
              <div className="form-grid">
                {/* JOB TITLE WITH AI SUGGESTION BUTTON */}
                <div className="job-title-wrapper" ref={wrapperRef}>
                  <div className="form-group job-title-group">
                    <label>Job Title *</label>

                    <div className="job-title-input-wrapper">
                      <input
                        type="text"
                        name="jobTitle"
                        value={form.jobTitle}
                        onChange={handleJobTitleChange}
                        placeholder="e.g. Frontend Developer"
                        required
                        className="job-title-input"
                        autoComplete="off"
                      />

                      {/* AI SUGGESTION BUTTON — always visible when not empty */}
                      {form.jobTitle.trim() !== "" && (
                        <button
                          type="button"
                          className="ai-suggestion-btn"
                          onClick={handleAISuggestion}
                        >
                          ✨ AI Suggestion
                        </button>
                      )}
                    </div>

                    {/* JOB TITLE DROPDOWN SUGGESTION LIST */}
                    {/* only render when there are filteredRoles to show */}
                    {filteredRoles.length > 0 && (
                      <ul className="title-suggestion-list">
                        {filteredRoles.map((role, index) => (
                          <li
                            key={index}
                            onClick={() => handleSelectJobRole(role)}
                            className="suggestion-item"
                          >
                            {role}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Job Type *</label>
                  <select
                    name="jobType"
                    value={form.jobType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Job Type</option>
                    <option>Full-Time</option>
                    <option>Part-Time</option>
                    <option>Internship</option>
                    <option>Contract</option>
                    <option>Remote</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Category</option>
                    <option>Software Development</option>
                    <option>Design</option>
                    <option>Marketing</option>
                    <option>Finance</option>
                    <option>Sales</option>
                    <option>Human Resources</option>
                    <option>Operations</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="e.g. Bangalore, India"
                    required
                  />
                </div>
              </div>
            </section>

            {/* Description Section */}
            <section className="form-section">
              <h3 className="section-title">Description</h3>
              <div className="form-group">
                <label>Job Description *</label>
                <textarea
                  name="description"
                  rows="4"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Describe job roles, expectations, and daily tasks..."
                  required
                ></textarea>
              </div>

              {/* Skills Field (Chips + Suggestions) */}
              <div className="form-group skills-field">
                <label>Skills *</label>
                <div className="skills-input-container">
                  {form.skills.map((skill, index) => (
                    <span key={index} className="skill-chip">
                      {skill}
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(skill)}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={skillInput}
                    onChange={handleSkillInputChange}
                    onKeyDown={handleSkillKeyDown}
                    placeholder="Type a skill and press Enter..."
                  />
                </div>

                {skillSuggestions.length > 0 && (
                  <ul className="suggestion-list">
                    {skillSuggestions.map((skill, idx) => (
                      <li key={idx} onClick={() => handleAddSkill(skill)}>
                        {skill}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group">
                <label>Requirements *</label>
                <textarea
                  name="requirements"
                  rows="3"
                  value={form.requirements}
                  onChange={handleChange}
                  placeholder="List required skills or experience..."
                  required
                ></textarea>
              </div>
            </section>

            {/* Additional Info Section */}
            <section className="form-section">
              <h3 className="section-title">Additional Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Salary Range *</label>
                  <input
                    type="text"
                    name="salaryRange"
                    value={form.salaryRange}
                    onChange={handleChange}
                    placeholder="e.g. ₹4,00,000 - ₹8,00,000 per annum"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Experience Required *</label>
                  <input
                    type="text"
                    name="experience"
                    value={form.experience}
                    onChange={handleChange}
                    placeholder="e.g. 2 - 5 years"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Qualifications *</label>
                  <input
                    type="text"
                    name="qualifications"
                    value={form.qualifications}
                    onChange={handleChange}
                    placeholder="e.g. B.Tech / MBA"
                    required
                  />
                </div>
              </div>
            </section>

            {/* Openings Section */}
            <section className="form-section">
              <h3 className="section-title">Openings & Deadline</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Number of Openings *</label>
                  <input
                    type="number"
                    name="openings"
                    value={form.openings}
                    onChange={handleChange}
                    placeholder="e.g. 3"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Application Deadline *</label>
                  <input
                    type="date"
                    name="deadline"
                    value={form.deadline}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </section>

            {/* Contact Info */}
            <section className="form-section">
              <h3 className="section-title">Contact Information</h3>
              <div className="form-group">
                <label>Contact Email *</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={handleChange}
                  placeholder="e.g. hr@company.com"
                  required
                />
              </div>
            </section>

            <div className="button-group">
              <button
                type="button"
                className="preview-btn"
                onClick={handlePreview}
              >
                <AiOutlineEye className="btn-icon" />
                Preview
              </button>
              <button
                type="button"
                className="back-btn"
                onClick={() => navigate("/recruiter-dashboard")}
              >
                Back
              </button>
              <button
                type="button"
                className="draft-btn"
                onClick={handleSaveDraft}
              >
                <FiSave className="btn-icon" />
                Save Draft
              </button>
              <button type="submit" className="publish-btn">
                <FaPaperPlane className="btn-icon" onClick={handlePublish} /> Post Job
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default JobFormPage;
